/** Estilos do preenchimento dos cortes: fundo e trama, por regra.
 *
 *  O preenchimento a cheio (preenchimentoDeCorte.ts) desenha a secção
 *  de cada elemento. Aqui decide-se COMO: um estilo base para tudo, e
 *  regras por classe IFC ou por material que o mudam para os elementos
 *  que apanham. A primeira regra que apanha ganha, como numa lista de
 *  filtros.
 *
 *  O estilo base por omissão é uma trama diagonal em fundo branco (Rui,
 *  18/10/2026: "prefiro que seja uma trama diagonal em fundo branco ou
 *  cinza"). As regras sugeridas seguem as convenções de desenho (betão
 *  pontilhado, isolamento em ondas, aço a cheio) e só entram quando se
 *  pedem.
 *
 *  Puro: o material de cada elemento lê-se do texto do IFC
 *  (`ModeloStep`), sem cena. */

import type { ModeloStep } from '@viewer/lib/step/modeloStep'
import { lerArgTexto, lerListaDeRefs, lerRef } from '@viewer/lib/step/textoStep'

export type Trama =
  | 'cheio'
  | 'diagonal'
  | 'diagonal-inversa'
  | 'cruzada'
  | 'horizontal'
  | 'vertical'
  | 'pontos'
  | 'betao'
  | 'isolamento'

export const TRAMAS: ReadonlyArray<{ valor: Trama; rotulo: string }> = [
  { valor: 'diagonal', rotulo: 'Diagonal' },
  { valor: 'diagonal-inversa', rotulo: 'Diagonal inversa' },
  { valor: 'cruzada', rotulo: 'Cruzada' },
  { valor: 'horizontal', rotulo: 'Horizontal' },
  { valor: 'vertical', rotulo: 'Vertical' },
  { valor: 'pontos', rotulo: 'Pontos' },
  { valor: 'betao', rotulo: 'Betão (pontilhado)' },
  { valor: 'isolamento', rotulo: 'Isolamento (ondas)' },
  { valor: 'cheio', rotulo: 'Cheio, sem trama' },
]

export interface EstiloDeCorte {
  /** Cor de fundo, #rrggbb. */
  fundo: string
  trama: Trama
  /** Cor do traço da trama, #rrggbb. */
  corDaTrama: string
}

/** Os dois fundos que o Rui pediu, com nome. */
export const FUNDOS: ReadonlyArray<{ valor: string; rotulo: string }> = [
  { valor: '#ffffff', rotulo: 'Branco' },
  { valor: '#c8ccd2', rotulo: 'Cinza' },
]

export const ESTILO_BASE_POR_OMISSAO: EstiloDeCorte = {
  fundo: '#ffffff',
  trama: 'diagonal',
  corDaTrama: '#3a414c',
}

export type TipoDeRegra = 'classe' | 'material' | 'lente'

export interface RegraDeCorte {
  id: string
  activa: boolean
  tipo: TipoDeRegra
  /** Classe: "IfcWall" (apanha também IfcWallStandardCase). Material:
   *  pedaços de texto separados por vírgulas ("betão, concrete, C30");
   *  apanha se algum aparecer no nome de algum material do elemento.
   *  Lente: o id de uma lente guardada; apanha os elementos que alguma
   *  regra activa da lente apanhe. */
  valor: string
  estilo: EstiloDeCorte
}

/** Regras sugeridas, pelas convenções de desenho. Os nomes de material
 *  vêm em português e inglês, que é como os modelos chegam. */
export const REGRAS_SUGERIDAS: ReadonlyArray<Omit<RegraDeCorte, 'id'>> = [
  {
    activa: true,
    tipo: 'material',
    valor: 'betão, betao, concrete, beton, C20/25, C25/30, C30/37, C35/45',
    estilo: { fundo: '#c8ccd2', trama: 'betao', corDaTrama: '#2f353e' },
  },
  {
    activa: true,
    tipo: 'material',
    valor: 'alvenaria, tijolo, bloco, masonry, brick, block',
    estilo: { fundo: '#ffffff', trama: 'diagonal', corDaTrama: '#8a3b2e' },
  },
  {
    activa: true,
    tipo: 'material',
    valor: 'isolamento, isolante, insulation, XPS, EPS, lã mineral, la mineral, mineral wool, rockwool',
    estilo: { fundo: '#fff8d6', trama: 'isolamento', corDaTrama: '#9a7a12' },
  },
  {
    activa: true,
    tipo: 'material',
    valor: 'aço, aco, steel, S235, S275, S355',
    estilo: { fundo: '#5b6370', trama: 'cheio', corDaTrama: '#5b6370' },
  },
  {
    activa: true,
    tipo: 'material',
    valor: 'madeira, wood, timber, CLT, glulam',
    estilo: { fundo: '#f3e3c3', trama: 'diagonal-inversa', corDaTrama: '#7a5524' },
  },
  {
    activa: true,
    tipo: 'material',
    valor: 'vidro, glass',
    estilo: { fundo: '#d7eef5', trama: 'cheio', corDaTrama: '#d7eef5' },
  },
]

/** Sem acentos e em minúsculas, para "Betão" apanhar "betao". */
export function normalizar(t: string): string {
  return t
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
}

/** Os pedaços de texto de uma regra de material. */
export function pedacos(valor: string): string[] {
  return valor
    .split(/[,;|]/)
    .map(normalizar)
    .filter((p) => p.length > 0)
}

/** A classe apanha? "IfcWall" apanha IFCWALL e IFCWALLSTANDARDCASE. */
export function classeApanha(valor: string, classe: string | null): boolean {
  if (!classe) return false
  const v = normalizar(valor).replace(/\s+/g, '')
  if (!v) return false
  return normalizar(classe).startsWith(v)
}

export function materialApanha(valor: string, materiais: readonly string[]): boolean {
  const ps = pedacos(valor)
  if (ps.length === 0 || materiais.length === 0) return false
  const ms = materiais.map(normalizar)
  return ps.some((p) => ms.some((m) => m.includes(p)))
}

/** O estilo de um elemento: a primeira regra activa que o apanha, senão
 *  o base. `materiais` só é chamado se houver uma regra de material, porque
 *  ler o texto do IFC custa. `apanhaLente` diz se a lente com aquele id
 *  apanha o elemento (sem ele, as regras de lente não apanham nada). */
export function estiloDoElemento(
  classe: string | null,
  materiais: () => readonly string[],
  regras: readonly RegraDeCorte[],
  base: EstiloDeCorte,
  apanhaLente?: (lenteId: string) => boolean,
): EstiloDeCorte {
  let mats: readonly string[] | null = null
  for (const r of regras) {
    if (!r.activa) continue
    if (r.tipo === 'classe') {
      if (classeApanha(r.valor, classe)) return r.estilo
    } else if (r.tipo === 'lente') {
      if (r.valor && apanhaLente?.(r.valor)) return r.estilo
    } else {
      mats ??= materiais()
      if (materialApanha(r.valor, mats)) return r.estilo
    }
  }
  return base
}

/** Uma chave curta por estilo, para juntar os elementos com o mesmo. */
export function chaveDoEstilo(e: EstiloDeCorte): string {
  return `${e.trama}|${e.fundo}|${e.corDaTrama}`
}

/** Hex válido? (#rrggbb) */
export function hexValido(t: string): boolean {
  return /^#[0-9a-f]{6}$/i.test(t)
}

/** Lê um estilo vindo de fora (sessão, armazenamento): o que não presta
 *  fica com o valor por omissão. */
export function lerEstilo(x: unknown, recurso: EstiloDeCorte = ESTILO_BASE_POR_OMISSAO): EstiloDeCorte {
  const o = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>
  const trama = TRAMAS.some((t) => t.valor === o.trama) ? (o.trama as Trama) : recurso.trama
  return {
    fundo: typeof o.fundo === 'string' && hexValido(o.fundo) ? o.fundo : recurso.fundo,
    trama,
    corDaTrama: typeof o.corDaTrama === 'string' && hexValido(o.corDaTrama) ? o.corDaTrama : recurso.corDaTrama,
  }
}

export function lerRegras(x: unknown): RegraDeCorte[] {
  if (!Array.isArray(x)) return []
  const out: RegraDeCorte[] = []
  for (const r of x) {
    if (!r || typeof r !== 'object') continue
    const o = r as Record<string, unknown>
    if (typeof o.id !== 'string' || typeof o.valor !== 'string') continue
    if (o.tipo !== 'classe' && o.tipo !== 'material' && o.tipo !== 'lente') continue
    out.push({ id: o.id, activa: o.activa !== false, tipo: o.tipo, valor: o.valor, estilo: lerEstilo(o.estilo) })
  }
  return out
}

// ---------------------------------------------------------------------
// Materiais de um elemento, a partir do texto do IFC
// ---------------------------------------------------------------------

export interface IndiceDeMateriais {
  /** Elemento ou tipo -> materiais associados (IfcRelAssociatesMaterial). */
  associados: Map<number, number[]>
  /** Elemento -> tipo (IfcRelDefinesByType). */
  tipoDe: Map<number, number>
}

export function indiceDeMateriais(m: ModeloStep): IndiceDeMateriais {
  const associados = new Map<number, number[]>()
  for (const rel of m.idsDaClasse('IFCRELASSOCIATESMATERIAL')) {
    const a = m.args(rel)
    const mat = lerRef(a[5])
    if (mat === null) continue
    for (const o of lerListaDeRefs(a[4])) {
      const l = associados.get(o)
      if (l) l.push(mat)
      else associados.set(o, [mat])
    }
  }
  const tipoDe = new Map<number, number>()
  for (const rel of m.idsDaClasse('IFCRELDEFINESBYTYPE')) {
    const a = m.args(rel)
    const tipo = lerRef(a[5])
    if (tipo === null) continue
    for (const o of lerListaDeRefs(a[4])) tipoDe.set(o, tipo)
  }
  return { associados, tipoDe }
}

/** Os nomes de todos os materiais por baixo de uma definição de
 *  material (camadas, perfis, constituintes, listas), e os nomes dos
 *  próprios conjuntos. */
export function nomesDoMaterial(m: ModeloStep, id: number, fundo = 0): string[] {
  if (fundo > 6 || !m.existe(id)) return []
  const a = m.args(id)
  const sub = (ref: string | undefined) => {
    const r = lerRef(ref)
    return r === null ? [] : nomesDoMaterial(m, r, fundo + 1)
  }
  const lista = (arg: string | undefined) => lerListaDeRefs(arg).flatMap((r) => nomesDoMaterial(m, r, fundo + 1))
  const nome = (arg: string | undefined) => {
    const t = lerArgTexto(arg)
    return t ? [t] : []
  }
  switch (m.classe(id)) {
    case 'IFCMATERIAL':
      return nome(a[0])
    case 'IFCMATERIALLAYERSETUSAGE':
    case 'IFCMATERIALPROFILESETUSAGE':
    case 'IFCMATERIALPROFILESETUSAGETAPERING':
      return sub(a[0])
    case 'IFCMATERIALLAYERSET':
      return [...lista(a[0]), ...nome(a[1])]
    case 'IFCMATERIALLAYER':
    case 'IFCMATERIALLAYERWITHOFFSETS':
      return [...sub(a[0]), ...nome(a[3])]
    case 'IFCMATERIALPROFILESET':
    case 'IFCMATERIALCONSTITUENTSET':
      return [...nome(a[0]), ...lista(a[2])]
    case 'IFCMATERIALPROFILE':
    case 'IFCMATERIALPROFILEWITHOFFSETS':
    case 'IFCMATERIALCONSTITUENT':
      return [...nome(a[0]), ...sub(a[2])]
    case 'IFCMATERIALLIST':
      return lista(a[0])
    default:
      return []
  }
}

/** Os materiais de um elemento: os dele e, se não tiver, os do tipo. */
export function materiaisDoElemento(m: ModeloStep, ix: IndiceDeMateriais, elemento: number): string[] {
  const proprios = (ix.associados.get(elemento) ?? []).flatMap((id) => nomesDoMaterial(m, id))
  if (proprios.length > 0) return proprios
  const tipo = ix.tipoDe.get(elemento)
  if (tipo === undefined) return []
  return (ix.associados.get(tipo) ?? []).flatMap((id) => nomesDoMaterial(m, id))
}
